import { useCallback, useEffect, useRef, useState } from 'react'
import { deployFetchBlob } from '@/api/guardianforge/deploy'

interface Props {
  comparisonId: string
  frameCount: number
  idx: number
  onIdxChange: (idx: number) => void
  metricsRow?: MetricsRow | null
}

export interface MetricsRow {
  processed_index?: string
  source_frame?: string
  model_a_ms?: string
  model_a_detections?: string
  model_a_avg_conf?: string
  model_a_max_conf?: string
  model_b_ms?: string
  model_b_detections?: string
  model_b_avg_conf?: string
  model_b_max_conf?: string
  [key: string]: string | undefined
}

const PLAY_INTERVAL_MS = 100
const PREFETCH_RADIUS = 1
const MAX_CACHED_FRAMES = 48

function revokeBlobUrls(urls: Iterable<string>) {
  for (const url of urls) {
    URL.revokeObjectURL(url)
  }
}

export function FrameInspector({ comparisonId, frameCount, idx, onIdxChange, metricsRow }: Props) {
  const [playing, setPlaying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const blobUrlsRef = useRef<Map<number, string>>(new Map())
  const inFlightRef = useRef<Map<number, Promise<void>>>(new Map())
  const generationRef = useRef(0)
  const [, forceTick] = useState(0)

  const pruneBlobCache = useCallback((keep: Set<number>) => {
    const cache = blobUrlsRef.current
    if (cache.size <= MAX_CACHED_FRAMES) return
    for (const [frameIdx, url] of cache) {
      if (cache.size <= MAX_CACHED_FRAMES) return
      if (keep.has(frameIdx)) continue
      URL.revokeObjectURL(url)
      cache.delete(frameIdx)
    }
  }, [])

  const fetchFrame = useCallback(
    async (target: number) => {
      if (target < 0 || target >= frameCount) return false
      if (blobUrlsRef.current.has(target)) return true

      const existing = inFlightRef.current.get(target)
      if (existing) {
        await existing
        return blobUrlsRef.current.has(target)
      }

      const generation = generationRef.current
      const promise = (async () => {
        try {
          const blob = await deployFetchBlob(`/comparisons/${comparisonId}/frames/${target}.jpg`)
          if (generation !== generationRef.current) return
          const url = URL.createObjectURL(blob)
          const previousUrl = blobUrlsRef.current.get(target)
          if (previousUrl) URL.revokeObjectURL(previousUrl)
          blobUrlsRef.current.set(target, url)
          pruneBlobCache(new Set([idx, target]))
          setError(null)
        } catch (err) {
          if (generation === generationRef.current) {
            setError(err instanceof Error ? err.message : String(err))
          }
        } finally {
          if (generation === generationRef.current) {
            inFlightRef.current.delete(target)
            forceTick((value) => value + 1)
          }
        }
      })()
      inFlightRef.current.set(target, promise)
      forceTick((value) => value + 1)
      await promise
      return blobUrlsRef.current.has(target)
    },
    [comparisonId, frameCount, idx, pruneBlobCache],
  )

  useEffect(() => {
    generationRef.current += 1
    revokeBlobUrls(blobUrlsRef.current.values())
    blobUrlsRef.current = new Map()
    inFlightRef.current = new Map()
    setPlaying(false)
    setError(null)
  }, [comparisonId])

  useEffect(() => {
    void fetchFrame(idx)
    for (let offset = 1; offset <= PREFETCH_RADIUS; offset += 1) {
      void fetchFrame(idx + offset)
      void fetchFrame(idx - offset)
    }
  }, [idx, fetchFrame])

  useEffect(() => {
    return () => {
      generationRef.current += 1
      revokeBlobUrls(blobUrlsRef.current.values())
      blobUrlsRef.current = new Map()
      inFlightRef.current = new Map()
    }
  }, [])

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const tag = (event.target as HTMLElement | null)?.tagName?.toLowerCase()
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        onIdxChange(Math.max(0, idx - 1))
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        onIdxChange(Math.min(frameCount - 1, idx + 1))
      } else if (event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault()
        setPlaying((value) => !value)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [idx, frameCount, onIdxChange])

  useEffect(() => {
    if (!playing) return
    let cancelled = false
    const timeout = window.setTimeout(() => {
      if (idx + 1 >= frameCount) {
        setPlaying(false)
        return
      }
      const advance = async () => {
        const nextIdx = idx + 1
        const ready = await fetchFrame(nextIdx)
        if (cancelled) return
        if (ready) {
          onIdxChange(nextIdx)
        } else {
          setPlaying(false)
        }
      }
      void advance()
    }, PLAY_INTERVAL_MS)
    return () => {
      cancelled = true
      window.clearTimeout(timeout)
    }
  }, [playing, idx, frameCount, fetchFrame, onIdxChange])

  const currentUrl = blobUrlsRef.current.get(idx)
  const currentLoading = inFlightRef.current.has(idx)

  if (frameCount <= 0) {
    return (
      <div className="deployment-muted">
        이 비교에는 프레임 시퀀스가 없습니다. 같은 candidate로 save_video 옵션을 켜고 다시 비교하세요.
      </div>
    )
  }

  return (
    <div className="deployment-frame-inspector">
      <div className="deployment-frame-canvas">
        {currentUrl ? (
          <img src={currentUrl} alt={`frame ${idx}`} />
        ) : (
          <div className="deployment-frame-loading deployment-muted">{currentLoading ? '로딩 중...' : '프레임 없음'}</div>
        )}
      </div>
      {error && (
        <div className="deployment-error-box with-actions">
          <span>frame: {error}</span>
          <button className="ghost" type="button" onClick={() => void fetchFrame(idx)}>
            Retry
          </button>
        </div>
      )}
      <div className="deployment-frame-controls">
        <button
          className="ghost"
          aria-label="이전 프레임"
          disabled={idx === 0}
          onClick={() => onIdxChange(Math.max(0, idx - 1))}
        >
          ‹
        </button>
        <input
          type="range"
          min={0}
          max={Math.max(0, frameCount - 1)}
          value={idx}
          onChange={(event) => onIdxChange(Number(event.target.value))}
        />
        <button
          className="ghost"
          aria-label="다음 프레임"
          disabled={idx + 1 >= frameCount}
          onClick={() => onIdxChange(Math.min(frameCount - 1, idx + 1))}
        >
          ›
        </button>
        <button className="ghost" aria-label={playing ? '정지' : '재생'} onClick={() => setPlaying((value) => !value)}>
          {playing ? 'Pause' : 'Play'}
        </button>
        <div className="deployment-mono deployment-frame-counter">
          {idx + 1} / {frameCount}
        </div>
      </div>
      <div className="deployment-muted deployment-frame-hint">← / → 키로 프레임 이동, Space 로 재생/정지</div>
      {metricsRow && <FrameMetricsTable row={metricsRow} />}
    </div>
  )
}

function FrameMetricsTable({ row }: { row: MetricsRow }) {
  const hasModelB = row.model_b_ms !== undefined
  return (
    <table className="deployment-table deployment-frame-metrics">
      <thead>
        <tr>
          <th>label</th>
          <th>ms</th>
          <th>detections</th>
          <th>avg conf</th>
          <th>max conf</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="deployment-mono">{hasModelB ? 'baseline' : 'candidate'}</td>
          <td className="deployment-mono">{fmt(row.model_a_ms)}</td>
          <td className="deployment-mono">{row.model_a_detections ?? '-'}</td>
          <td className="deployment-mono">{fmt(row.model_a_avg_conf)}</td>
          <td className="deployment-mono">{fmt(row.model_a_max_conf)}</td>
        </tr>
        {hasModelB && (
          <tr>
            <td className="deployment-mono">candidate</td>
            <td className="deployment-mono">{fmt(row.model_b_ms)}</td>
            <td className="deployment-mono">{row.model_b_detections ?? '-'}</td>
            <td className="deployment-mono">{fmt(row.model_b_avg_conf)}</td>
            <td className="deployment-mono">{fmt(row.model_b_max_conf)}</td>
          </tr>
        )}
      </tbody>
    </table>
  )
}

function fmt(value: string | undefined): string {
  if (value === undefined) return '-'
  const num = Number(value)
  if (!Number.isFinite(num)) return value
  return num.toFixed(2)
}
